import "zod/compile";
import { z } from "zod";
import {
  CASH_DIMENSION_DEFINITIONS,
  CASH_MEASURE_DEFINITIONS,
  CASH_UNIT_DEFINITIONS,
  CashDimensionSchema,
  CashFiltersSchema,
  CashMeasureSchema,
  CashRangeSchema,
  type CashAggregateQuery,
  type CashDimension,
  type CashDimensionDefinition,
  type CashMeasure,
  type CashMeasureDefinition,
} from "./ar-cash-dashboard.grammar";
import { breakdownBars, breakdownHeight } from "./ar-cash-dashboard.template";
import type { ReportWidgetInput } from "@erp/shared-types";
import { CASH_QUERY_CATALOG } from "./ar-cash-dashboard.source";

/**
 * Chart presets: a few house-style cash charts described by their fields
 * rather than by a hand-written Vega-Lite spec, so a chart Nami (or anyone)
 * adds reads like the template's. Each builds a canvas custom chart: its
 * cash `source` (query and range pin) and its spec;
 * the theme's colors and fonts are applied when the chart renders.
 *
 * - `bar`: one measure by a category, as the template's breakdown bars
 *   (label, bar, the value and the row's share beside it). Fixed-value
 *   dimensions keep their own order; others are ranked, largest first.
 * - `column`: one count or dollar measure per day, week or month, a column
 *   each (a day with nothing keeps its slot).
 * - `stacked_bar`: one count or dollar measure per period, stacked by a
 *   fixed-value dimension (optionally as shares of each period).
 * - `line`: one measure per period, for a rate or a typical value (a share,
 *   a median) whose trend matters more than each period's total.
 */

export const CASH_CHART_PRESETS = [
  "bar",
  "column",
  "stacked_bar",
  "line",
] as const;

const TimeDimensionSchema = z.enum(["day", "week", "month"]);
type TimeDimension = z.infer<typeof TimeDimensionSchema>;

const BarPresetSchema = z.strictObject({
  chart: z.literal("bar"),
  /** The category the bars are for (not a time dimension). */
  category: CashDimensionSchema,
  /** What the bars' length shows. */
  value: CashMeasureSchema,
  /** A count shown beside a money value, e.g. receipt_count beside receipt_amount. */
  count: CashMeasureSchema.optional(),
  /** Ranked categories (customer, bank): how many bars. */
  limit: z.int().min(1).max(20).optional(),
});

const ColumnPresetSchema = z.strictObject({
  chart: z.literal("column"),
  time: TimeDimensionSchema,
  value: CashMeasureSchema,
});

const StackedBarPresetSchema = z.strictObject({
  chart: z.literal("stacked_bar"),
  time: TimeDimensionSchema,
  /** A dimension with a fixed set of values (payment_type, keyed_by, …). */
  series: CashDimensionSchema,
  value: CashMeasureSchema,
  /** Show each period as shares of 100% rather than totals. */
  normalize: z.boolean().optional(),
});

const LinePresetSchema = z.strictObject({
  chart: z.literal("line"),
  time: TimeDimensionSchema,
  value: CashMeasureSchema,
});

export const CashChartPresetSchema = z.discriminatedUnion("chart", [
  BarPresetSchema,
  ColumnPresetSchema,
  StackedBarPresetSchema,
  LinePresetSchema,
]);
export type CashChartPreset = z.infer<typeof CashChartPresetSchema>;

/** A chart widget written as a preset: its text, the preset, and the query's filters and range. */
export const CashChartWidgetInputSchema = z.strictObject({
  id: z.string().min(1).max(100),
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  chart: CashChartPresetSchema,
  filters: CashFiltersSchema.optional(),
  /** Pins the widget's range; absent = the page range. */
  range: CashRangeSchema.optional(),
});
export type CashChartWidgetInput = z.infer<typeof CashChartWidgetInputSchema>;

type Spec = Record<string, unknown>;

/** A preset that the grammar cannot draw, in words a person (or Nami) can act on. */
export class CashChartPresetError extends Error {}

function measure(id: CashMeasure): CashMeasureDefinition {
  return CASH_MEASURE_DEFINITIONS[id];
}

function dimension(id: CashDimension): CashDimensionDefinition {
  return CASH_DIMENSION_DEFINITIONS[id];
}

/** A fixed-value dimension's values in order, with their labels. */
function fixedValues(id: CashDimension): [string | null, string][] | null {
  const { values, valueLabels } = dimension(id);
  if (!values) return null;
  return values.map((value) => [
    value,
    valueLabels?.[value ?? "null"] ?? String(value),
  ]);
}

const TIME_FORMAT: Record<TimeDimension, string> = {
  day: "%b %-d",
  week: "Week of %b %-d",
  month: "%b %Y",
};
/** A recessive value axis: light grid, no domain line or ticks, the unit's format. */
function valueAxis(format: string): Spec {
  return {
    title: null,
    format,
    grid: true,
    gridOpacity: 0.5,
    domain: false,
    ticks: false,
    tickCount: 4,
  };
}

/**
 * The period axis. Days are a time scale, so a day with nothing keeps its
 * slot; weeks and months are one evenly spaced band each (a time scale would
 * draw a week's column one day wide). Labels thin out rather than collide.
 */
function timeX(time: TimeDimension): Spec {
  const axis = {
    title: null,
    labelAngle: 0,
    labelOverlap: "greedy",
    labelSeparation: 8,
    ticks: false,
    domain: false,
    grid: false,
  };
  if (time === "day") {
    return {
      field: time,
      type: "temporal",
      timeUnit: "utcyearmonthdate",
      axis: { ...axis, format: TIME_FORMAT.day, tickCount: 6 },
    };
  }
  return {
    field: time,
    type: "ordinal",
    sort: "ascending",
    scale: { paddingInner: 0.3 },
    axis: {
      ...axis,
      labelExpr: `utcFormat(toDate(datum.value), ${JSON.stringify(TIME_FORMAT[time])})`,
    },
  };
}

const SERIES_LEGEND = { orient: "top", title: null, symbolType: "square" };

/** A column mark: rounded ends and a gap between neighbours, as the template's. */
function columnMark(time: TimeDimension, rounded: boolean): Spec {
  return {
    type: "bar",
    ...(rounded ? { cornerRadiusEnd: 2 } : {}),
    // Days sit on a time scale: leave a fifth of each day's slot as the gap.
    ...(time === "day" ? { width: { band: 0.8 } } : {}),
  };
}

function barSpec(preset: z.infer<typeof BarPresetSchema>) {
  const value = measure(preset.value);
  if (!value.dimensions.includes(preset.category)) {
    throw new CashChartPresetError(
      `Measure "${preset.value}" can't be broken down by ${preset.category}.`,
    );
  }
  if (preset.count && measure(preset.count).unit !== "count") {
    throw new CashChartPresetError(`"${preset.count}" is not a count.`);
  }
  const categories = fixedValues(preset.category);
  const rows = categories?.length ?? preset.limit ?? 8;
  const additive = value.unit === "count" || value.unit === "money";
  const measures = [preset.value, ...(preset.count ? [preset.count] : [])];
  return {
    measures,
    dimensions: [preset.category],
    spec: breakdownBars({
      category: preset.category,
      ...(categories
        ? { categories }
        : {
            rows,
            labelField:
              preset.category === "customer" ? "customer_name" : undefined,
          }),
      value: preset.value,
      count:
        preset.count ?? (value.unit === "count" ? preset.value : undefined),
      // Dollars beside a separate count; alone, the value label shows them.
      amount: value.unit === "money" && preset.count ? preset.value : undefined,
      valueFormat: CASH_UNIT_DEFINITIONS[value.unit].vegaFormat,
      countTitle: preset.count ? measure(preset.count).label : value.label,
      noShare: !additive,
    }),
    plotHeight: breakdownHeight(rows),
  };
}

/** The measure as a number in `v` (money arrives as a decimal string). */
function numericValue(id: CashMeasure): Spec {
  return { calculate: `toNumber(datum[${JSON.stringify(id)}])`, as: "v" };
}

/** The period's label, for tooltips. */
function periodLabel(time: TimeDimension): Spec {
  return {
    calculate: `utcFormat(toDate(datum[${JSON.stringify(time)}]), ${JSON.stringify(TIME_FORMAT[time])})`,
    as: "periodLabel",
  };
}

/** The tooltip line naming a period. */
function periodTooltip(time: TimeDimension): Spec {
  return { field: "periodLabel", title: dimension(time).label };
}

function requireSlices(id: CashMeasure, dimensions: CashDimension[]) {
  for (const d of dimensions) {
    if (!measure(id).dimensions.includes(d)) {
      throw new CashChartPresetError(
        `Measure "${id}" can't be sliced by ${d}.`,
      );
    }
  }
}

function requireTotal(id: CashMeasure, chart: string) {
  const { unit } = measure(id);
  if (unit !== "count" && unit !== "money") {
    throw new CashChartPresetError(
      `A ${chart} chart shows counts or dollars; "${id}" is ${unit}. Use a line.`,
    );
  }
}

function columnSpec(preset: z.infer<typeof ColumnPresetSchema>) {
  requireTotal(preset.value, "column");
  requireSlices(preset.value, [preset.time]);
  const value = measure(preset.value);
  const format = CASH_UNIT_DEFINITIONS[value.unit].vegaFormat;
  return {
    measures: [preset.value],
    dimensions: [preset.time],
    spec: {
      transform: [numericValue(preset.value), periodLabel(preset.time)],
      // As the template's daily filing chart: rounded column ends, a slot
      // for every period, a light value grid.
      mark: columnMark(preset.time, true),
      encoding: {
        x: timeX(preset.time),
        y: { field: "v", type: "quantitative", axis: valueAxis(format) },
        tooltip: [
          periodTooltip(preset.time),
          { field: "v", title: value.label, format },
        ],
      },
    },
  };
}

function stackedBarSpec(preset: z.infer<typeof StackedBarPresetSchema>) {
  const value = measure(preset.value);
  const series = fixedValues(preset.series);
  if (!series) {
    throw new CashChartPresetError(
      `Stack by a dimension with fixed values, not ${preset.series}.`,
    );
  }
  requireTotal(preset.value, "stacked");
  requireSlices(preset.value, [preset.time, preset.series]);
  const format = preset.normalize
    ? ".0%"
    : CASH_UNIT_DEFINITIONS[value.unit].vegaFormat;
  const labels = series.map(([, label]) => label);
  const field = JSON.stringify(preset.series);
  const labelCases = series.reduceRight(
    (rest, [v, label]) =>
      `datum[${field}] === ${JSON.stringify(v)} ? ${JSON.stringify(label)} : ${rest}`,
    '"Other"',
  );
  return {
    measures: [preset.value],
    dimensions: [preset.time, preset.series],
    spec: {
      transform: [
        numericValue(preset.value),
        periodLabel(preset.time),
        { calculate: labelCases, as: "seriesLabel" },
        {
          calculate: `indexof(${JSON.stringify(labels)}, datum.seriesLabel)`,
          as: "stackOrder",
        },
      ],
      mark: columnMark(preset.time, false),
      encoding: {
        x: timeX(preset.time),
        y: {
          field: "v",
          type: "quantitative",
          stack: preset.normalize ? "normalize" : "zero",
          axis: valueAxis(format),
        },
        // Two series in one hue, the first full and the second a light tint,
        // as the template's daily filing chart; more get the categorical
        // colors in the dimension's fixed order.
        ...(labels.length === 2
          ? {
              opacity: {
                field: "seriesLabel",
                type: "nominal",
                scale: { domain: labels, range: [1, 0.3] },
                legend: SERIES_LEGEND,
              },
            }
          : {
              color: {
                field: "seriesLabel",
                type: "nominal",
                scale: { domain: labels },
                legend: SERIES_LEGEND,
              },
            }),
        order: { field: "stackOrder" },
        tooltip: [
          periodTooltip(preset.time),
          { field: "seriesLabel", title: dimension(preset.series).label },
          {
            field: "v",
            title: value.label,
            format: CASH_UNIT_DEFINITIONS[value.unit].vegaFormat,
          },
        ],
      },
    },
  };
}

function lineSpec(preset: z.infer<typeof LinePresetSchema>) {
  requireSlices(preset.value, [preset.time]);
  const value = measure(preset.value);
  const format = CASH_UNIT_DEFINITIONS[value.unit].vegaFormat;
  const tooltip = [
    periodTooltip(preset.time),
    { field: "v", title: value.label, format },
  ];
  return {
    measures: [preset.value],
    dimensions: [preset.time],
    spec: {
      transform: [numericValue(preset.value), periodLabel(preset.time)],
      encoding: { x: timeX(preset.time) },
      layer: [
        {
          mark: { type: "line", strokeWidth: 2, interpolate: "linear" },
          encoding: {
            y: { field: "v", type: "quantitative", axis: valueAxis(format) },
          },
        },
        {
          // Hover: the nearest period gets a rule and a point, with its value.
          params: [
            {
              name: "hover",
              select: {
                type: "point",
                encodings: ["x"],
                nearest: true,
                on: "pointerover",
                clear: "pointerout",
              },
            },
          ],
          mark: { type: "point", size: 64, filled: true },
          encoding: {
            y: { field: "v", type: "quantitative" },
            opacity: {
              condition: { param: "hover", empty: false, value: 1 },
              value: 0,
            },
            tooltip,
          },
        },
        {
          transform: [{ filter: { param: "hover", empty: false } }],
          mark: { type: "rule", strokeDash: [3, 3], opacity: 0.5 },
        },
      ],
    },
  };
}

function presetChart(preset: CashChartPreset) {
  switch (preset.chart) {
    case "bar":
      return barSpec(preset);
    case "column":
      return columnSpec(preset);
    case "stacked_bar":
      return stackedBarSpec(preset);
    case "line":
      return lineSpec(preset);
  }
}

/**
 * The custom chart a preset describes: a `vega-chart` widget whose source is
 * the preset's cash query (its measures and dimensions, with the given
 * filters) pinned to the given range, and whose spec is the house-style
 * Vega-Lite. Throws {@link CashChartPresetError} for a preset the grammar
 * cannot draw; the widget schema still validates the result.
 */
export function cashChartWidget(
  input: CashChartWidgetInput,
): Extract<ReportWidgetInput, { type: "vega-chart" }> {
  const built = presetChart(input.chart);
  const query: CashAggregateQuery = {
    kind: "aggregate",
    measures: built.measures,
    dimensions: built.dimensions,
    filters: input.filters ?? {},
  };
  return {
    type: "vega-chart",
    id: input.id,
    title: input.title,
    ...(input.description ? { description: input.description } : {}),
    source: {
      catalog: CASH_QUERY_CATALOG,
      query,
      ...(input.range ? { range: input.range } : {}),
    },
    grammar: "vega-lite",
    spec: built.spec,
    ...("plotHeight" in built ? { plotHeight: built.plotHeight } : {}),
  };
}
